<template>
    <form @submit.prevent="submit">
        <div class="bg-white py-6 px-4 space-y-6 sm:p-6">
            <slot />
            <div class="grid grid-cols-4 gap-6">
                <ui-input
                    name="title"
                    :label="$t('forms.account.title')"
                    class="col-span-4"
                    v-model="account.title"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.title">
                        {{ $t(account.errors.title[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="email"
                    :label="$t('forms.account.email')"
                    class="col-span-4"
                    type="email"
                    v-model="account.email"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.email">
                        {{ $t(account.errors.email[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="phone"
                    :label="$t('forms.account.phone')"
                    class="col-span-4"
                    v-model="account.phone"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.phone">
                        {{ $t(account.errors.phone[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="address"
                    :label="$t('forms.account.address')"
                    class="col-span-4 sm:col-span-2"
                    v-model="account.address"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.address">
                        {{ $t(account.errors.address[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="city"
                    :label="$t('forms.account.city')"
                    class="col-span-4 sm:col-span-2"
                    v-model="account.city"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.city">
                        {{ $t(account.errors.city[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="province"
                    :label="$t('forms.account.province')"
                    class="col-span-4 sm:col-span-2"
                    v-model="account.province"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.province">
                        {{ $t(account.errors.province[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="postal_code"
                    :label="$t('forms.account.postal_code')"
                    class="col-span-4 sm:col-span-2"
                    v-model="account.postal_code"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.postal_code">
                        {{ $t(account.errors.postal_code[0]) }}
                    </div>
                </ui-input>
                <ui-input
                    name="slogan"
                    :label="$t('forms.account.slogan')"
                    class="col-span-4"
                    v-model="account.slogan"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.slogan">
                        {{ $t(account.errors.slogan[0]) }}
                    </div>
                </ui-input>
                <ui-textarea
                    name="description"
                    :label="$t('forms.account.description')"
                    class="col-span-4"
                    :rows="3"
                    v-model="account.description"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.description">
                        {{ $t(account.errors.description[0]) }}
                    </div>
                </ui-textarea>
                <div class="col-span-2">
                    <label class="block text-sm leading-5 font-medium text-gray-700" for="logo">
                        {{ $t('forms.account.logo') }}
                    </label>
                    <div class="mt-2 sm:mt-0 sm:col-span-2">
                        <div class="flex items-center">
                            <avatar :path="account.logo" custom-class="w-24" :avatar="false" />
                            <span class="ml-5 rounded-md shadow-sm">
                                <input
                                    id="logo"
                                    accept="image/*"
                                    class="hidden"
                                    type="file"
                                    ref="logo"
                                    @change="onChangeUpload"
                                />
                                <activix-button @click="$refs.logo.click()">
                                    {{ $t('forms.account.upload') }}
                                </activix-button>
                            </span>
                        </div>
                        <div class="text-sm text-red-500" v-if="account.errors.logo">
                            {{ $t(account.errors.logo[0]) }}
                        </div>
                    </div>
                </div>
                <ui-input
                    name="facebook"
                    :label="$t('forms.account.facebook')"
                    class="col-span-2"
                    v-model="account.facebook"
                >
                    <div class="text-sm text-red-500" v-if="account.errors.facebook">
                        {{ $t(account.errors.facebook[0]) }}
                    </div>
                </ui-input>
            </div>
        </div>
        <div class="px-4 py-3 bg-gray-50 text-right flex justify-between sm:px-6">
            <slot name="leftButton" :is-edited="isEdited">
                <div />
            </slot>
            <span class="inline-flex rounded-md shadow-sm">
                <activix-button :loading="account.processing" color="indigo" type="submit">
                    {{ submitText }}
                </activix-button>
            </span>
        </div>
    </form>
</template>

<script>
    import isEqual from 'lodash/isEqual.js';
    import Avatar from '@/components/ui/Avatar.vue';
    import ActivixButton from '@/components/ui/ActivixButton.vue';
    import UiInput from '@/components/ui/UiInput.vue';
    import UiTextarea from '@/components/ui/UiTextarea.vue';

    export default {
        components: {
            UiTextarea,
            UiInput,
            ActivixButton,
            Avatar,
        },

        props: {
            initialAccount: {
                type: Object,
                default: () => {},
            },
            submitText: {
                type: String,
                default: 'Save',
            },
        },

        data: (vm) => ({
            account: vm.$inertia.form({ ...vm.initialAccount }),
        }),

        computed: {
            isEdited() {
                return !isEqual(this.initialAccount, this.account.data());
            },
        },

        watch: {
            initialAccount() {
                if (Object.keys(this.$page.props.errors).length) {
                    return;
                }

                this.account = this.$inertia.form({ ...this.initialAccount });
            },
        },

        methods: {
            onChangeUpload(e) {
                const file = e.target.files[0];
                this.account.logo = file;
            },

            submit() {
                this.account.transform((data) => ({
                    ...data,
                    _method: 'put',
                })).post(this.$route(), {
                    onSuccess: () => this.$emit('updated'),
                });
            },
        },
    };
</script>
